import type { useIdoListStore } from 'src/stores/idoList';
import type { useWorkspaceTabsStore } from 'src/stores/workspaceTabs';

import type { Router } from 'vue-router';

import { EventBus } from 'src/util/eventBus/EventBus';
import { QueryStore } from 'src/util/query/QueryStore';
import { RECORDS_SEARCH_MARKER_QUERY_KEY, RECORDS_SEARCH_TAB_ID } from 'src/util/workspaceTabs/tabIdentity';

type IdoListStore = ReturnType<typeof useIdoListStore>;
type WorkspaceTabsStore = ReturnType<typeof useWorkspaceTabsStore>;

interface IdoGlobalSearchClearInput {
	idoListStore: IdoListStore;
	queryStore: QueryStore | undefined;
}

interface IdoGlobalSearchInput extends IdoGlobalSearchClearInput {
	eventBus?: EventBus;
	router?: Router;
	searchTerm: string;
	workspaceTabsStore?: WorkspaceTabsStore;
}

const updateOpenSearchWorkspaceTab = (input: IdoGlobalSearchInput, trimmedTerm: string): void => {
	if (input.workspaceTabsStore === undefined || input.router === undefined) {
		return;
	}

	const searchTab = input.workspaceTabsStore.tabs.find((tab) => tab.id === RECORDS_SEARCH_TAB_ID);
	if (searchTab === undefined) {
		return;
	}

	const instanceId = searchTab.params.instanceId;
	if (typeof instanceId !== 'string' || instanceId.trim() === '' || instanceId === 'all') {
		return;
	}

	const params = { instanceId };
	const query = { [`search_${instanceId}`]: trimmedTerm, [RECORDS_SEARCH_MARKER_QUERY_KEY]: '1' };
	input.workspaceTabsStore.updateRoute(RECORDS_SEARCH_TAB_ID, params, query);

	if (input.workspaceTabsStore.activeTabId === RECORDS_SEARCH_TAB_ID) {
		void input.router.replace({ name: 'records', params, query });
	}
};

export const clearIdoGlobalSearch = async (input: IdoGlobalSearchClearInput): Promise<void> => {
	input.idoListStore.setGlobalSearchTerm('');
	input.idoListStore.clearGlobalSearchResults();
	input.idoListStore.setGlobalSearchExplicitlyCleared(true);
	input.idoListStore.setSearchInProgress(false);
	await input.idoListStore.fetchIdosInInstanceCountList({ queryStore: input.queryStore });
};

export const executeIdoGlobalSearch = async (input: IdoGlobalSearchInput): Promise<void> => {
	const trimmedTerm = input.searchTerm.trim();

	if (trimmedTerm === '') {
		await clearIdoGlobalSearch({ idoListStore: input.idoListStore, queryStore: input.queryStore });
		return;
	}

	input.idoListStore.setSearchInProgress(true);
	input.idoListStore.setGlobalSearchTerm(trimmedTerm);
	input.idoListStore.setGlobalSearchExplicitlyCleared(false);
	input.idoListStore.setGlobalSearchFocusedInstanceId(null);
	input.eventBus?.emit('activateIdoSidebarSearchTab');
	updateOpenSearchWorkspaceTab(input, trimmedTerm);
	await input.idoListStore.fetchIdoList({
		idoInstanceId: 'all',
		limit: 20,
		page: 1,
		queryStore: input.queryStore,
		searchTerm: trimmedTerm,
		serializeIdoIdoListFilter: '',
		sort: [],
	});
};
